import axios, {
  type RawAxiosRequestHeaders,
  type AxiosInstance,
  type AxiosRequestHeaders,
  type AxiosResponse,
  type AxiosError,
} from 'axios'

import { useUserSession } from '/@src/stores/userSession'
import { useAuth } from '/@src/stores/auth'
import { useRouter } from 'vue-router'

let api: AxiosInstance
let refreshing = false
let refreshQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
}> = []

export function createApi(url?: string) {
  // Here we set the base URL for all requests made to the api
  api = axios.create({
    baseURL: url ?? import.meta.env.VITE_API_BASE_URL ?? 'https://api-kazeeai.spiderops.id/api',
  })

  // We set an interceptor for each request to
  // include Bearer token to the request if user is logged in
  api.interceptors.request.use((config) => {
    const userSession = useUserSession()

    if (userSession.isLoggedIn) {
      config.headers = {
        ...((config.headers as RawAxiosRequestHeaders) ?? {}),
        Authorization: `Bearer ${userSession.token}`,
      } as AxiosRequestHeaders
    }

    return config
  })

  // Response interceptor to handle token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const userSession = useUserSession()
      const auth = useAuth()
      const router = useRouter()
      const originalRequest = error.config

      // If error is 401 Unauthorized and we have a refresh token
      if (error.response?.status === 401 && 
          userSession.refreshToken && 
          originalRequest && 
          !(originalRequest as any)._retry) {
        
        if (refreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject })
          }).then(() => {
            return api(originalRequest)
          }).catch((err) => {
            return Promise.reject(err)
          })
        }

        // Set refreshing flag
        refreshing = true;
        (originalRequest as any)._retry = true

        try {
          // Attempt to refresh the token
          const success = await auth.refreshAuthToken()
          
          if (success) {
            // Update headers with new token
            (originalRequest.headers as AxiosRequestHeaders).Authorization = 
              `Bearer ${userSession.token}`
            
            // Process queued requests
            refreshQueue.forEach(({ resolve }) => resolve(true))
            refreshQueue = []
            
            // Retry original request with new token
            return api(originalRequest)
          } else {
            // If refresh fails, redirect to login
            refreshQueue.forEach(({ reject }) => 
              reject(new Error('Token refresh failed'))
            )
            refreshQueue = []
            router.push('/auth/login')
            return Promise.reject(error)
          }
        } catch (refreshError) {
          // If refresh request fails, redirect to login
          refreshQueue.forEach(({ reject }) => 
            reject(new Error('Token refresh failed'))
          )
          refreshQueue = []
          router.push('/auth/login')
          return Promise.reject(refreshError)
        } finally {
          refreshing = false
        }
      }

      // For other errors, just reject the promise
      return Promise.reject(error)
    }
  )

  return api
}

export function useApi(url?: string) {
  if (!api) {
    createApi(url)
  }
  return api
}